import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Tours', path: '#tours' },
    { name: 'Services', path: '#services' },
    { name: 'Gallery', path: '#gallery' },
    { name: 'Contact', path: '#contact' }
  ];

  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false);
    if (path.startsWith('#')) {
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className={`flex items-center justify-between gap-4 transition-all duration-300 ${
          isScrolled ? 'h-20 py-2' : 'h-36 py-6'
        }`}>
          {/* Logo - High Quality Cape Atlantic Tours Logo */}
          <Link to="/" className={`flex items-center flex-shrink-0 z-10 transition-all duration-300 ${
            isScrolled ? 'mt-0' : 'mt-4'
          }`}>
            <img 
              src="/cape-atlantic-logo.png" 
              alt="Cape Atlantic Tours" 
              className={`w-auto object-contain transition-all duration-300 ${
                isScrolled ? 'h-16' : 'h-28'
              }`}
              style={{ imageRendering: 'high-quality' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 flex-1 justify-center z-10">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.path}
                onClick={(e) => {
                  if (item.path.startsWith('#')) {
                    e.preventDefault();
                    handleNavClick(item.path);
                  }
                }}
                className={`font-medium transition-colors duration-300 hover:text-[#6B0B0B] ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-3 flex-shrink-0 z-10">
            <a 
              href="https://wa.me/27736540090" 
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center space-x-2 transition-colors duration-300 ${
                isScrolled ? 'text-gray-700 hover:text-[#6B0B0B]' : 'text-white hover:text-[#8B1515]'
              }`}
            >
              <Phone size={16} />
              <span className="text-sm font-medium whitespace-nowrap">073 654 0090</span>
            </a>
            <Button 
              onClick={() => handleNavClick('#contact')}
              className="bg-[#6B0B0B] hover:bg-[#8B1515] text-white whitespace-nowrap px-4 py-2"
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors duration-300 flex-shrink-0 ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg rounded-lg mt-2 py-4 px-4">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.path}
                  onClick={(e) => {
                    if (item.path.startsWith('#')) {
                      e.preventDefault();
                      handleNavClick(item.path);
                    }
                  }}
                  className="text-gray-700 font-medium hover:text-[#6B0B0B] transition-colors"
                >
                  {item.name}
                </a>
              ))}
              <div className="pt-4 border-t border-gray-200">
                <a 
                  href="https://wa.me/27736540090"
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="flex items-center space-x-2 text-gray-700 hover:text-[#6B0B0B] mb-3"
                >
                  <Phone size={18} />
                  <span className="text-sm font-medium">073 654 0090</span>
                </a>
                <Button 
                  onClick={() => handleNavClick('#contact')}
                  className="w-full bg-[#6B0B0B] hover:bg-[#8B1515] text-white"
                >
                  Book Now
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
